import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import { DynamicLevel, DynamicXAlign, DynamicYAlign } from '../../../../enums';
import type { TextDynamicCircleRendererSettings } from '../renderer/TextDynamicCircleRenderer';
import { TextDynamicCircleRenderer } from '../renderer/TextDynamicCircleRenderer';
import type { Pixels } from '../../../../types';
import { assertNumber } from '../../../../validators';
import type { LoadSettings } from '../../../serializableMixin';
import { int2rgba } from '../../../../libs/videoData';
import { calculateRadiusAfterFit } from '../textMetricsHelpers';
import { GradientPoint } from '../../../GradientPoint';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

export class TextAssetDynamicCircle extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicCircle';

    override readonly type = Config.CIRCLE_GRAPHIC;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor radius = 0;
    @ColorAttribute()
    accessor strokeColor = 0xffffff;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fill = 0xff00ff;
    @AlphaAttribute()
    accessor fillAlpha = 0.5;
    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    accessor fillType: 'solid' | 'linear' = 'solid';
    @StringAttribute({ choices: ['fixed', 'autoResize'], animatable: false })
    accessor radiusMode: 'fixed' | 'autoResize' = 'fixed';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    #radiusPadding: number | null = null;
    #radiusAfterFit: number | null = null;

    /** The gap kept between the text and the circle edge, in reference units; `null` until a build captures it. */
    get radiusPadding(): number | null {
        return this.#radiusPadding;
    }

    set radiusPadding(value: number | null) {
        if (this.#radiusPadding === value) return;
        if (value !== null) assertNumber(value);
        this.#radiusPadding = value;
        this.requiresUpdate = true;
    }

    /** The radius the circle fitted its text at on its last build, for the editor to display; `null` while it isn't fitting. Nothing renders from it. */
    get radiusAfterFit(): number | null {
        return this._isAutoResizing() ? this.#radiusAfterFit : null;
    }

    override load(dataObj: Record<string, unknown>, settings?: LoadSettings): void {
        this.#radiusPadding = null;
        this.#radiusAfterFit = null;
        super.load(dataObj, settings);
    }

    /** Only a centred circle can fit its text: any other alignment offsets the centre by the radius, so fitting would walk the circle off its text. */
    get canAutoResize(): boolean {
        return (
            this.canFitGraphicsToText &&
            this.level === DynamicLevel.object &&
            this.alignX === DynamicXAlign.center &&
            (this.alignY === DynamicYAlign.center || this.alignY === DynamicYAlign.averageCenter)
        );
    }

    /** @internal */
    _isAutoResizing(): boolean {
        return this.canAutoResize && this.radiusMode === 'autoResize';
    }

    /** Whether the circle fits its text right now, which additionally needs a settle radius it can resolve. */
    get isFittingToText(): boolean {
        return this._isAutoResizing() && this.#radiusBeforeFit() !== null;
    }

    /** @internal Captures the gap the authored design already leaves around its text, so enabling auto-resize doesn't move the circle. */
    _captureRadiusPadding(referenceHalfDiagonal: number): void {
        if (!this._isAutoResizing()) return;
        const radiusBeforeFit = this.#radiusBeforeFit();
        if (radiusBeforeFit === null) return;

        const radiusPadding = this.#radiusPadding ?? radiusBeforeFit - referenceHalfDiagonal;
        if (this.#radiusPadding === null) {
            this.radiusPadding = radiusPadding;
        }
        this.#radiusAfterFit = calculateRadiusAfterFit(referenceHalfDiagonal, radiusPadding);
    }

    /** `null` when an animation settles on an inferred dimension, which only the renderer can resolve. */
    #radiusBeforeFit(): number | null {
        const radiusAnimation = this.animationConfigurations
            .filter((animation) => animation.enabled && animation.property === 'radius')
            .at(-1);
        if (!radiusAnimation) return this.radius;
        if (typeof radiusAnimation.endValue !== 'number') return null;
        const settledRadius = radiusAnimation.endValue * radiusAnimation.endMulti;
        return settledRadius + (radiusAnimation.shouldOffsetPropertyValue ? this.radius : 0);
    }

    override getCanvasRendererSettings(): TextDynamicCircleRendererSettings {
        const base = super.getCanvasRendererSettings();

        // make the stroke invisible for zero width
        const alpha = this.strokeWidth > 0 ? this.strokeAlpha : 0;
        const style = int2rgba(this.strokeColor, alpha);
        const radiusBeforeFit = this.#radiusBeforeFit();
        const isFitting = this.isFittingToText;

        return {
            ...base,
            fill: int2rgba(this.fill, this.fillAlpha),
            type: this.type,
            radius: (this.radius * this.referenceToOutputMultiplier) as Pixels,
            radiusPadding:
                isFitting && this.#radiusPadding !== null
                    ? ((this.#radiusPadding * this.referenceToOutputMultiplier) as Pixels)
                    : null,
            radiusBeforeFit:
                isFitting && radiusBeforeFit !== null
                    ? ((radiusBeforeFit * this.referenceToOutputMultiplier) as Pixels)
                    : null,
            strokeStyle: style,
            lineWidth: (this.strokeWidth * this.referenceToOutputMultiplier) as Pixels,
            fillType: this.fillType,
            gradientStartStop: int2rgba(
                this.gradientStartPoint.color,
                this.gradientStartPoint.alpha,
            ),
            gradientEndStop: int2rgba(this.gradientEndPoint.color, this.gradientEndPoint.alpha),
            gradientStartPoint: { x: this.gradientStartPoint.x, y: this.gradientStartPoint.y },
            gradientEndPoint: { x: this.gradientEndPoint.x, y: this.gradientEndPoint.y },
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this))
            .concat(['gradientStartPoint', 'gradientEndPoint', 'radiusPadding']);
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since TextDynamicCircle is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return TextDynamicCircleRenderer.animatableProperties;
    }

    override get animatableGeometricProperties() {
        return TextDynamicCircleRenderer.animatableGeometricProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'fill',
            'fillAlpha',
            'strokeColor',
            'strokeAlpha',
            'fillType',
            'gradientStartPoint',
            'gradientEndPoint',
        ]);
    }
}
